import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DISPATCH_CHANNEL_LABELS,
  DISPATCH_STATUS_LABELS,
  describeDispatchTemplate,
  type DispatchEntry,
  type DispatchFilterOptions,
} from '@azko/shared';

import { ApiRequestError, api } from '../../lib/api.js';
import { useSession } from '../../lib/session.js';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  LoadingRows,
  SearchInput,
  Select,
  StatusBadge,
  TextInput,
  cx,
} from '../../components/ui.js';

/**
 * O registro de disparos.
 *
 * Responde a pergunta que mais chega ao suporte: "o cliente diz que nao
 * recebeu — saiu ou nao saiu?". Ate aqui a resposta morava em dois lugares e
 * nenhum era uma tela: o log do container, que ninguem le as 3h, e a saude por
 * canal, que da o agregado e nao sabe nada sobre UMA mensagem.
 *
 * O CONTEUDO DA MENSAGEM NAO APARECE, e a ausencia e deliberada. Enquanto a
 * linha espera na fila o payload ainda tem a senha temporaria e o link de
 * recuperacao — eles so saem no envio. Como esta lista mostra linha em qualquer
 * estado, exibir o corpo entregaria credencial ativa a quem abriu a tela so
 * para conferir se um e-mail saiu.
 *
 * O destinatario, ao contrario, aparece INTEIRO. Mascarado, a tela deixa de
 * responder "foi para o endereco certo?" — que e metade do motivo de ela
 * existir. Quem chega aqui ja tem `plataforma:logs:visualizar` e le o log de
 * auditoria inteiro; nao e dado novo.
 */

interface DispatchesResponse {
  items: DispatchEntry[];
  nextCursor: string | null;
  /** So vem na primeira pagina; nas seguintes a tela ja tem. */
  filters: DispatchFilterOptions | null;
}

const PERIODS = [
  { value: '1', label: 'Ultimas 24 horas' },
  { value: '7', label: 'Ultimos 7 dias' },
  { value: '30', label: 'Ultimos 30 dias' },
  { value: '90', label: 'Ultimos 90 dias' },
  { value: 'todos', label: 'Desde o comeco' },
];

const CHANNELS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'E-mail' },
];

const STATUSES = [
  { value: 'queued', label: 'Na fila' },
  { value: 'sending', label: 'Enviando' },
  { value: 'sent', label: 'Enviado' },
  { value: 'delivered', label: 'Entregue' },
  { value: 'read', label: 'Lido' },
  { value: 'failed', label: 'Falhou' },
];

/**
 * `failed` e o unico que precisa gritar — e a linha em que alguem NAO foi
 * avisado. `queued` e `sending` sao estado normal de passagem e viram cinza:
 * pintar de amarelo tudo que ainda nao saiu deixaria a tela alarmada o tempo
 * todo em operacao saudavel, e ai ninguem olha mais.
 */
function statusTone(status: DispatchEntry['status']) {
  if (status === 'failed') return 'warn';
  if (status === 'sent' || status === 'delivered' || status === 'read') return 'accent';
  return 'neutral';
}

function formatMoment(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function DispatchesPage() {
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [template, setTemplate] = useState('');
  const [condominium, setCondominium] = useState('');
  const [days, setDays] = useState('7');
  const [search, setSearch] = useState('');

  /**
   * Reenviar, em dois passos e com motivo.
   *
   * A tela era so leitura, e o comentario do router explicava por que reenviar
   * daqui "parece obvio e nao e": credencial reenviada carregaria uma senha que
   * ja pode ter sido trocada. A resposta nao foi deixar de reenviar nada — foi
   * a API recusar os templates que carregam segredo. O que sobrava sem isto era
   * uma sessao de WhatsApp caida derrubar avisos em lote e nao haver forma
   * nenhuma de manda-los de novo depois de reparear.
   */
  const [resending, setResending] = useState<string | null>(null);
  const [resendReason, setResendReason] = useState('');
  const [resendError, setResendError] = useState<string | null>(null);
  const [resent, setResent] = useState<string[]>([]);
  const [pages, setPages] = useState<string[]>([]);

  const before = pages[pages.length - 1] ?? null;
  // Busca de uma letra so nao filtra nada e custa um LIKE na tabela que mais
  // cresce no produto. O backend recusa, e a tela nem chega a pedir.
  const effectiveSearch = search.trim().length >= 2 ? search.trim() : '';

  /** Trocar qualquer filtro volta para a primeira pagina: o cursor pertencia ao
   *  conjunto anterior e continuaria paginando a lista antiga. */
  function reset<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      setPages([]);
    };
  }

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      'admin', 'disparos', channel, status, template, condominium, days, effectiveSearch, before,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (channel) params.set('channel', channel);
      if (status) params.set('status', status);
      if (template) params.set('template', template);
      if (condominium) params.set('condominium', condominium);
      if (days) params.set('days', days);
      if (effectiveSearch) params.set('search', effectiveSearch);
      if (before) params.set('before', before);
      return api.get<DispatchesResponse>(`/admin/disparos?${params.toString()}`);
    },
    // As opcoes de filtro so viajam na primeira pagina; sem isto, paginar
    // esvaziaria os selects.
    placeholderData: (previous) => previous,
  });

  const [options, setOptions] = useState<DispatchFilterOptions | null>(null);
  if (data?.filters && data.filters !== options) setOptions(data.filters);

  const queryClient = useQueryClient();
  const { staff } = useSession();
  const podeReenviar = staff?.permissions.includes('plataforma:assinantes:editar') ?? false;

  const resend = useMutation({
    mutationFn: (id: string) =>
      api.post<{ id: number }>(`/admin/disparos/${id}/reenviar`, {
        reason: resendReason.trim(),
      }),
    onSuccess: (_result, id) => {
      setResending(null);
      setResendReason('');
      setResendError(null);
      // Marca a linha na tela: a antiga continua `failed` de proposito, entao
      // sem isto nada mudaria de aparencia e o clique pareceria nao ter feito
      // nada.
      setResent((atual) => [...atual, id]);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'disparos'] });
    },
    onError: (cause) => {
      setResendError(
        cause instanceof ApiRequestError ? cause.message : 'Nao consegui reenviar.',
      );
    },
  });

  const items = data?.items ?? [];
  const filtering =
    Boolean(channel) || Boolean(status) || Boolean(template) || Boolean(condominium) ||
    Boolean(effectiveSearch);

  return (
    <>
      <div>
        <p className="eyebrow">Plataforma</p>
        <h1 className="mt-2 text-2xl font-extrabold tracking-tight sm:text-3xl">Disparos.</h1>
        <p className="mt-2 text-sm text-muted">
          Cada mensagem que o sistema mandou — WhatsApp de encomenda, credencial de acesso,
          recuperacao de senha — com o estado real de cada uma. O conteudo da mensagem nao aparece
          aqui de proposito: enquanto ela espera na fila, o corpo ainda carrega senha e link de
          recuperacao.
        </p>
      </div>

      <Card className="p-4 sm:p-5">
        {/* Canal como botao e nao select: e o filtro que muda o SIGNIFICADO da
            lista, e precisa mostrar o estado sem abrir nada. */}
        <div className="flex flex-wrap gap-2">
          {CHANNELS.map((item) => {
            const on = channel === item.value;
            return (
              <button
                key={item.value}
                type="button"
                onClick={() => {
                  setChannel(on ? '' : item.value);
                  setPages([]);
                }}
                aria-pressed={on}
                className={cx(
                  'rounded-pill border px-4 py-2 text-sm font-semibold transition sm:px-3 sm:py-1.5',
                  on
                    ? 'border-accent bg-accent/15 text-accent'
                    : 'border-line text-muted hover:border-line-strong hover:text-fg',
                )}
              >
                {item.label}
              </button>
            );
          })}
          {/* Falhas primeiro: e o unico recorte que alguem procura com pressa. */}
          <button
            type="button"
            onClick={() => {
              setStatus(status === 'failed' ? '' : 'failed');
              setPages([]);
            }}
            aria-pressed={status === 'failed'}
            className={cx(
              'rounded-pill border px-4 py-2 text-sm font-semibold transition sm:px-3 sm:py-1.5',
              status === 'failed'
                ? 'border-warn bg-warn/15 text-warn'
                : 'border-line text-muted hover:border-line-strong hover:text-fg',
            )}
          >
            So as falhas
          </button>
          {filtering && (
            <Button
              variant="quiet"
              className="py-1.5"
              onClick={() => {
                setChannel('');
                setStatus('');
                setTemplate('');
                setCondominium('');
                setSearch('');
                setPages([]);
              }}
            >
              Limpar filtros
            </Button>
          )}
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            value={condominium}
            onChange={reset(setCondominium)}
            emptyOption="Todos os condominios"
            options={(options?.condominiums ?? []).map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            aria-label="Filtrar por condominio"
          />

          <Select
            value={template}
            onChange={reset(setTemplate)}
            emptyOption="Todas as mensagens"
            options={(options?.templates ?? []).map((item) => ({
              value: item,
              label: describeDispatchTemplate(item),
            }))}
            aria-label="Filtrar por tipo de mensagem"
          />

          <Select
            value={status}
            onChange={reset(setStatus)}
            emptyOption="Todos os estados"
            options={STATUSES}
            aria-label="Filtrar por estado"
          />

          <Select value={days} onChange={reset(setDays)} options={PERIODS} aria-label="Periodo" />
        </div>

        <div className="mt-3">
          <SearchInput
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPages([]);
            }}
            placeholder="Telefone ou e-mail do destinatario"
            aria-label="Buscar destinatario"
          />
        </div>
      </Card>

      {isLoading && <LoadingRows rows={6} />}

      {!isLoading && items.length === 0 && (
        <EmptyState
          title="Nada neste recorte"
          description={
            filtering
              ? 'Nenhum disparo combina com os filtros. Tente ampliar o periodo ou limpar o canal.'
              : 'Nenhum disparo no periodo escolhido. Aumente a janela para ver o que veio antes.'
          }
        />
      )}

      {items.length > 0 && (
        <Card className="overflow-hidden p-0">
          <ul className="divide-y divide-line-soft">
            {items.map((item) => (
              <li key={item.id} className="px-4 py-4 sm:px-6">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <StatusBadge tone={statusTone(item.status)}>
                    {DISPATCH_STATUS_LABELS[item.status]}
                  </StatusBadge>
                  <span className="font-semibold text-fg">
                    {describeDispatchTemplate(item.template)}
                  </span>
                  <span className="text-xs text-faint">
                    {DISPATCH_CHANNEL_LABELS[item.channel]}
                  </span>
                </div>

                <p className="mt-1.5 break-all text-sm text-muted">
                  {/* Inteiro, sem mascara: a pergunta e "foi para o endereco
                      certo?", e mascarado nao da para conferir. */}
                  <span className="font-medium text-fg">{item.recipient}</span>
                  {item.condominiumName && <span className="text-faint"> · {item.condominiumName}</span>}
                </p>

                {/* O erro so aparece quando existe, e inteiro: e a unica linha
                    que diz por que alguem nao foi avisado. */}
                {item.lastError && (
                  <p className="mt-1.5 break-words text-sm text-warn">{item.lastError}</p>
                )}

                {podeReenviar && item.status === 'failed' && (
                  <div className="mt-2">
                    {resent.includes(item.id) ? (
                      <span className="text-sm text-accent">
                        Reenviado — a mensagem nova esta na fila.
                      </span>
                    ) : resending === item.id ? (
                      <div className="space-y-2 rounded-2xl border border-line bg-surface-2 p-3">
                        <p className="text-sm text-muted">
                          Entra na fila como mensagem NOVA. Esta linha continua aqui com o erro
                          — ela e o registro de que falhou.
                        </p>
                        <TextInput
                          value={resendReason}
                          onChange={(event) => setResendReason(event.target.value)}
                          placeholder="Motivo (obrigatorio)"
                          aria-label="Motivo do reenvio"
                          maxLength={255}
                        />
                        {resendError && <Alert>{resendError}</Alert>}
                        <div className="flex flex-wrap gap-2">
                          <Button
                            className="py-1.5"
                            disabled={resendReason.trim().length < 3}
                            loading={resend.isPending}
                            onClick={() => resend.mutate(item.id)}
                          >
                            Reenviar
                          </Button>
                          <Button
                            variant="ghost"
                            className="py-1.5"
                            onClick={() => {
                              setResending(null);
                              setResendError(null);
                            }}
                          >
                            Voltar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="quiet"
                        className="py-1"
                        onClick={() => {
                          setResending(item.id);
                          setResendReason('');
                          setResendError(null);
                        }}
                      >
                        Reenviar
                      </Button>
                    )}
                  </div>
                )}

                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-faint tabular-nums">
                  <span>criado {formatMoment(item.createdAt)}</span>
                  {item.sentAt && <span>enviado {formatMoment(item.sentAt)}</span>}
                  {/* Uma tentativa e o normal. Mostrar "1" em toda linha seria
                      ruido; a partir de duas o numero conta uma historia. */}
                  {item.attempts > 1 && <span>{item.attempts} tentativas</span>}
                  {item.providerMessageId && (
                    <span className="break-all" title="Id no provedor — use para achar a mensagem no painel dele">
                      {item.providerMessageId}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-3 border-t border-line-soft px-4 py-4 sm:px-6">
            {pages.length > 0 && (
              <Button variant="ghost" onClick={() => setPages((p) => p.slice(0, -1))}>
                Voltar
              </Button>
            )}
            {data?.nextCursor && (
              <Button
                variant="ghost"
                loading={isFetching}
                onClick={() => setPages((p) => [...p, data.nextCursor as string])}
              >
                Mais antigos
              </Button>
            )}
            {pages.length > 0 && (
              <Button variant="quiet" onClick={() => setPages([])}>
                Voltar ao inicio
              </Button>
            )}
          </div>
        </Card>
      )}
    </>
  );
}
